/**
 * Inventory Items API
 */

import { apiFetch, buildQueryString, API_BASE } from './client';
import type {
  InventoryItem,
  InventoryKpis,
  PaginatedResponse,
  InventoryItemFilters,
  CreateInventoryItemDto,
  UpdateInventoryItemDto,
  AdjustStockDto,
  ReserveStockDto,
  InventoryAdjustment,
} from '../types/inventory';

/**
 * Get inventory KPIs
 */
export const getInventoryKpis = async (): Promise<InventoryKpis> => {
  return apiFetch<InventoryKpis>(`${API_BASE}/kpis`);
};

/**
 * Get inventory items with filters
 */
export const getInventoryItems = async (
  params?: InventoryItemFilters
): Promise<PaginatedResponse<InventoryItem>> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedResponse<InventoryItem>>(`${API_BASE}${query}`);
};

/**
 * Get inventory item by ID
 */
export const getInventoryItem = async (id: string): Promise<InventoryItem> => {
  return apiFetch<InventoryItem>(`${API_BASE}/${id}`);
};

/**
 * Get inventory item by product ID
 */
export const getInventoryItemByProduct = async (productId: string): Promise<InventoryItem> => {
  return apiFetch<InventoryItem>(`${API_BASE}/product/${productId}`);
};

/**
 * Create inventory item
 */
export const createInventoryItem = async (
  data: CreateInventoryItemDto
): Promise<InventoryItem> => {
  return apiFetch<InventoryItem>(`${API_BASE}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

/**
 * Update inventory item
 */
export const updateInventoryItem = async (
  id: string,
  data: UpdateInventoryItemDto
): Promise<InventoryItem> => {
  return apiFetch<InventoryItem>(`${API_BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

/**
 * Delete inventory item
 */
export const deleteInventoryItem = async (id: string): Promise<void> => {
  return apiFetch<void>(`${API_BASE}/${id}`, {
    method: 'DELETE',
  });
};

/**
 * Adjust stock
 */
export const adjustStock = async (
  id: string,
  data: AdjustStockDto
): Promise<{ item: InventoryItem; adjustment: InventoryAdjustment }> => {
  return apiFetch<{ item: InventoryItem; adjustment: InventoryAdjustment }>(
    `${API_BASE}/${id}/adjust`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
};

/**
 * Reserve stock
 */
export const reserveStock = async (
  id: string,
  data: ReserveStockDto
): Promise<InventoryItem> => {
  return apiFetch<InventoryItem>(`${API_BASE}/${id}/reserve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

/**
 * Release reservation
 */
export const releaseReservation = async (
  id: string,
  data: ReserveStockDto
): Promise<InventoryItem> => {
  return apiFetch<InventoryItem>(`${API_BASE}/${id}/release`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

/**
 * Check stock availability
 */
export const checkAvailability = async (
  id: string,
  quantity: number
): Promise<{
  inventoryItemId: string;
  productId: string;
  sku: string;
  quantityOnHand: number;
  quantityAvailable: number;
  canFulfill: boolean;
  shortfall: number;
}> => {
  return apiFetch(`${API_BASE}/${id}/availability?quantity=${quantity}`);
};
